import { useModeAwareQuery, useModeAwareMutation } from "./useModeAwareQuery";
import { useApi } from "./useApi";
import { useQueryClient } from "@tanstack/react-query";
import { generateMockCases } from "@/lib/mock-data/collections-mock-data";
import type { CollectionCase } from "@/types/collections";

// API Response types
interface CollectionCaseApiResponse {
  caseId: string;
  caseNumber: string;
  customerId: number;
  customerName: string;
  customerAccountNumber: string | null;
  priority: string;
  status: string;
  strategyId: number | null;
  strategyName: string | null;
  amount: number;
  daysInCollection: number;
  nextAction: string | null;
  nextActionDate: string | null;
  riskLevel: string | null;
  isPaused: boolean;
  assignedTo: number | null;
  assignedToName: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string | null;
  activityCount: number;
}

interface CollectionCaseDashboardApiResponse {
  totalCases: number;
  openCases: number;
  closedCases: number;
  pausedCases: number;
  totalAmountInCollection: number;
  averageAmountPerCase: number;
  highPriorityCases: number;
  overdueCases: number;
  casesWithPromises: number;
}

interface GetCollectionCasesApiResponse {
  data: CollectionCaseApiResponse[];
  pagination: {
    page: number;
    pageSize: number;
    totalItems: number;  // Backend returns TotalItems, not totalCount
    totalPages: number;
  };
  dashboard: CollectionCaseDashboardApiResponse;
}

export interface GetCollectionCasesParams extends Record<string, string | number | boolean | undefined> {
  page?: number;
  pageSize?: number;
  search?: string;
  status?: string;
  priority?: string;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
}

export interface CollectionCasesListResult {
  cases: CollectionCase[];
  dashboard: CollectionCaseDashboardApiResponse;
  pagination: {
    page: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
  };
}

// Map API status to UI status
function mapApiStatusToUiStatus(status: string): CollectionCase['status'] {
  const statusMap: Record<string, CollectionCase['status']> = {
    'open': 'Active',
    'active': 'Active',
    'on_hold': 'On Hold',
    'on hold': 'On Hold',
    'closed': 'Closed',
    'promised': 'Promised',
    'escalated': 'Escalated',
  };
  return statusMap[status.toLowerCase()] ?? 'Active';
}

// Map API priority to UI priority
function mapApiPriorityToUi(priority: string): CollectionCase['priority'] {
  const priorityMap: Record<string, CollectionCase['priority']> = {
    'high': 'High',
    'medium': 'Medium',
    'low': 'Low',
  };
  return priorityMap[priority.toLowerCase()] ?? 'Medium';
}

// Map API risk level to UI risk level
function mapApiRiskLevelToUi(riskLevel: string | null | undefined): CollectionCase['riskLevel'] {
  if (!riskLevel) {
    return 'Medium'; // Default to Medium if riskLevel is null/undefined
  }
  const riskMap: Record<string, CollectionCase['riskLevel']> = {
    'high': 'High',
    'medium': 'Medium',
    'low': 'Low',
  };
  return riskMap[riskLevel.toLowerCase()] ?? 'Medium';
}

// Map API response to UI model
function mapCollectionCaseApiToUi(api: CollectionCaseApiResponse): CollectionCase {
  return {
    id: api.caseId,
    caseId: api.caseNumber,
    customerId: String(api.customerId),
    customerName: api.customerName,
    customerEmail: '',
    customerPhone: '',
    priority: mapApiPriorityToUi(api.priority),
    status: mapApiStatusToUiStatus(api.status),
    strategy: {
      id: api.strategyId ? String(api.strategyId) : '1',
      name: api.strategyName ?? 'Standard Collection',
      type: 'Standard',
    },
    strategyPhase: {
      currentPhase: 1,
      totalPhases: 4,
      phaseName: 'Initial Contact',
    },
    amount: api.amount,
    daysInCollection: api.daysInCollection,
    nextAction: api.nextAction ?? 'Review case',
    nextActionDate: api.nextActionDate ? new Date(api.nextActionDate) : new Date(),
    riskLevel: mapApiRiskLevelToUi(api.riskLevel),
    createdAt: new Date(api.createdAt),
    updatedAt: api.updatedAt ? new Date(api.updatedAt) : new Date(api.createdAt),
    isPaused: api.isPaused,
    assignedTo: api.assignedToName ?? undefined,
    notes: api.notes ?? undefined,
  };
}

// Generate mock data
const mockCases = generateMockCases();
const mockDashboard: CollectionCaseDashboardApiResponse = {
  totalCases: mockCases.length,
  openCases: mockCases.filter(c => c.status === 'Active').length,
  closedCases: mockCases.filter(c => c.status === 'Closed').length,
  pausedCases: mockCases.filter(c => c.isPaused).length,
  totalAmountInCollection: mockCases.reduce((sum, c) => sum + c.amount, 0),
  averageAmountPerCase: mockCases.length > 0 ? mockCases.reduce((sum, c) => sum + c.amount, 0) / mockCases.length : 0,
  highPriorityCases: mockCases.filter(c => c.priority === 'High').length,
  overdueCases: mockCases.filter(c => {
    const today = new Date();
    return new Date(c.nextActionDate) < today;
  }).length,
  casesWithPromises: mockCases.filter(c => c.status === 'Promised').length,
};

export function useCollectionCasesList(params?: GetCollectionCasesParams) {
  const { apiGet } = useApi();

  // Ensure default pagination and sort parameters are always provided
  const queryParams: GetCollectionCasesParams = {
    page: params?.page ?? 1,
    pageSize: params?.pageSize ?? 25,
    sortBy: params?.sortBy ?? 'createdAt',
    sortOrder: params?.sortOrder ?? 'desc',
    ...params,
  };

  return useModeAwareQuery<CollectionCasesListResult, Error>(
    ["collection-cases", queryParams],
    async ({ signal }) => {
      const response = await apiGet<GetCollectionCasesApiResponse>("/api/v1/collection-cases", queryParams, signal);
      const mappedCases = response.data.map(mapCollectionCaseApiToUi);
      return {
        cases: mappedCases,
        dashboard: response.dashboard,
        pagination: {
          page: response.pagination.page,
          pageSize: response.pagination.pageSize,
          totalCount: response.pagination.totalItems,  // Map totalItems to totalCount for UI
          totalPages: response.pagination.totalPages,
        },
      };
    },
    {
      cases: mockCases,
      dashboard: mockDashboard,
      pagination: {
        page: 1,
        pageSize: 25,
        totalCount: mockCases.length,
        totalPages: Math.ceil(mockCases.length / 25),
      },
    },
    {
      staleTime: 0,
      refetchOnMount: 'always',
    }
  );
}

export function useCollectionCaseById(caseId: string | null) {
  const { apiGet } = useApi();
  const mockCase = mockCases.find(c => c.id === caseId || c.caseId === caseId) ?? null;

  return useModeAwareQuery<CollectionCase | null, Error>(
    ["collection-case", caseId],
    async ({ signal }) => {
      if (!caseId) return null;
      const response = await apiGet<CollectionCaseApiResponse>(`/api/v1/collection-cases/${caseId}`, undefined, signal);
      return mapCollectionCaseApiToUi(response);
    },
    mockCase,
    {
      enabled: !!caseId,
      staleTime: 0,
      refetchOnMount: 'always',
    }
  );
}

export interface CreateCollectionCaseRequest {
  customerId: number;
  priority: string;
  strategyId?: number;
  amount: number;
  nextAction?: string;
  nextActionDate?: string;
  riskLevel: string;
  notes?: string;
}

export function useCreateCollectionCase() {
  const { apiPost } = useApi();
  const queryClient = useQueryClient();

  return useModeAwareMutation<CollectionCase, Error, CreateCollectionCaseRequest>(
    async (data) => {
      const response = await apiPost<CollectionCaseApiResponse>("/api/v1/collection-cases", data);
      return mapCollectionCaseApiToUi(response);
    },
    async (data) => {
      return {
        id: `CC-${Date.now()}`,
        caseId: `CC-${Date.now()}`,
        customerId: String(data.customerId),
        customerName: 'New Customer',
        customerEmail: '',
        customerPhone: '',
        priority: data.priority as CollectionCase['priority'],
        status: 'Active' as const,
        strategy: {
          id: String(data.strategyId ?? 1),
          name: 'Standard Collection',
          type: 'Standard' as const,
        },
        strategyPhase: {
          currentPhase: 1,
          totalPhases: 4,
          phaseName: 'Initial Contact',
        },
        amount: data.amount,
        daysInCollection: 0,
        nextAction: data.nextAction ?? 'Review case',
        nextActionDate: data.nextActionDate ? new Date(data.nextActionDate) : new Date(),
        riskLevel: data.riskLevel as CollectionCase['riskLevel'],
        createdAt: new Date(),
        updatedAt: new Date(),
        isPaused: false,
        notes: data.notes,
      };
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["collection-cases"] });
      },
    }
  );
}

export function useUpdateCollectionCase(caseId: string) {
  const { apiPut } = useApi();
  const queryClient = useQueryClient();

  return useModeAwareMutation<CollectionCase, Error, Partial<CreateCollectionCaseRequest>>(
    async (data) => {
      const response = await apiPut<CollectionCaseApiResponse>(`/api/v1/collection-cases/${caseId}`, data);
      return mapCollectionCaseApiToUi(response);
    },
    async () => {
      const existingCase = mockCases.find(c => c.id === caseId || c.caseId === caseId);
      return existingCase ?? mockCases[0];
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["collection-cases"] });
        queryClient.invalidateQueries({ queryKey: ["collection-case", caseId] });
      },
    }
  );
}

export function usePauseCollectionCase() {
  const { apiPost } = useApi();
  const queryClient = useQueryClient();

  return useModeAwareMutation<CollectionCase, Error, string>(
    async (caseId) => {
      const response = await apiPost<CollectionCaseApiResponse>(`/api/v1/collection-cases/${caseId}/pause`, {});
      return mapCollectionCaseApiToUi(response);
    },
    async (caseId) => {
      const existingCase = mockCases.find(c => c.id === caseId || c.caseId === caseId);
      return existingCase ? { ...existingCase, isPaused: true } : mockCases[0];
    },
    {
      onSuccess: (_data, caseId) => {
        queryClient.invalidateQueries({ queryKey: ["collection-cases"] });
        queryClient.invalidateQueries({ queryKey: ["collection-case", caseId] });
      },
    }
  );
}

export function useResumeCollectionCase() {
  const { apiPost } = useApi();
  const queryClient = useQueryClient();

  return useModeAwareMutation<CollectionCase, Error, string>(
    async (caseId) => {
      const response = await apiPost<CollectionCaseApiResponse>(`/api/v1/collection-cases/${caseId}/resume`, {});
      return mapCollectionCaseApiToUi(response);
    },
    async (caseId) => {
      const existingCase = mockCases.find(c => c.id === caseId || c.caseId === caseId);
      return existingCase ? { ...existingCase, isPaused: false } : mockCases[0];
    },
    {
      onSuccess: (_data, caseId) => {
        queryClient.invalidateQueries({ queryKey: ["collection-cases"] });
        queryClient.invalidateQueries({ queryKey: ["collection-case", caseId] });
      },
    }
  );
}

export function useCloseCollectionCase() {
  const { apiPost } = useApi();
  const queryClient = useQueryClient();

  return useModeAwareMutation<CollectionCase, Error, { caseId: string; notes?: string }>(
    async ({ caseId, notes }) => {
      const response = await apiPost<CollectionCaseApiResponse>(`/api/v1/collection-cases/${caseId}/close`, { notes });
      return mapCollectionCaseApiToUi(response);
    },
    async ({ caseId }) => {
      const existingCase = mockCases.find(c => c.id === caseId || c.caseId === caseId);
      return existingCase ? { ...existingCase, status: 'Closed' as const } : mockCases[0];
    },
    {
      onSuccess: (_data, { caseId }) => {
        queryClient.invalidateQueries({ queryKey: ["collection-cases"] });
        queryClient.invalidateQueries({ queryKey: ["collection-case", caseId] });
      },
    }
  );
}

export function useCollectionCasesDashboard() {
  const { apiGet } = useApi();

  return useModeAwareQuery<CollectionCaseDashboardApiResponse, Error>(
    ["collection-cases-dashboard"],
    async ({ signal }) => {
      return apiGet<CollectionCaseDashboardApiResponse>("/api/v1/collection-cases/dashboard", undefined, signal);
    },
    mockDashboard,
    {
      staleTime: 30 * 1000,
      refetchOnMount: 'always',
    }
  );
}
